import { useCallback, useMemo, useState } from 'react';
import { AlertTriangle, Loader2, ShieldOff } from 'lucide-react';
import type { BulkAlertAction } from '@kirin/api-client/contracts/alerts';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import {
  DURATION_DROPDOWN_LABELS,
  type DurationPreset,
  endOfDayUTC,
  tomorrowUtcStart,
  untilDays,
  untilOneMonth,
} from '@/lib/durationPresets';
import { bulkModalCopy } from './bulkActionCopy';

/** Duration handed to the apply hook — only one field is consumed per action. */
export interface BulkActionDuration {
  mutedUntil?: string | null;
  excludedUntil?: string | null;
}

export interface BulkActionConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  action: BulkAlertAction;
  eligibleCount: number;
  alreadyCount: number;
  isPending: boolean;
  /** Called on confirm. `duration` is present only for mute/exclude. */
  onConfirm: (duration?: BulkActionDuration) => void;
}

/** Preset pills, in ticket order. */
const DURATION_PRESETS: ReadonlyArray<{ key: DurationPreset; label: string }> =
  [
    { key: 'forever', label: DURATION_DROPDOWN_LABELS.forever },
    { key: '1_day', label: DURATION_DROPDOWN_LABELS['1_day'] },
    { key: '1_week', label: DURATION_DROPDOWN_LABELS['1_week'] },
    { key: '1_month', label: DURATION_DROPDOWN_LABELS['1_month'] },
    { key: 'custom', label: DURATION_DROPDOWN_LABELS.custom },
  ];

/** Resolve a preset (+ optional custom date) to the ISO sent to the backend. */
function resolveDurationIso(
  preset: DurationPreset,
  customDate: Date | undefined
): string | null {
  switch (preset) {
    case 'forever':
      return null;
    case '1_day':
      return untilDays(1);
    case '1_week':
      return untilDays(7);
    case '1_month':
      return untilOneMonth();
    case 'custom':
      return customDate ? endOfDayUTC(customDate) : null;
  }
}

/**
 * Confirmation modal for a single bulk action (KIR-3252). Parameterized by
 * `action` — one component covers mute / unmute / exclude / remove-exclusion;
 * they differ only by copy (from {@link bulkModalCopy}), the destructive
 * styling (exclude), and whether the duration picker is shown (mute / exclude).
 * The duration picker is an inline segmented-pill row + (on "Custom…") an inline
 * calendar, matching the ticket mockup. All-or-nothing → a single Confirm.
 */
export function BulkActionConfirmDialog({
  open,
  onOpenChange,
  action,
  eligibleCount,
  alreadyCount,
  isPending,
  onConfirm,
}: BulkActionConfirmDialogProps): React.ReactElement {
  const copy = useMemo(
    () => bulkModalCopy(action, { eligibleCount, alreadyCount }),
    [action, eligibleCount, alreadyCount]
  );

  const [preset, setPreset] = useState<DurationPreset>('forever');
  const [customDate, setCustomDate] = useState<Date | undefined>(undefined);
  const minDate = useMemo(() => tomorrowUtcStart(), []);

  const selectPreset = useCallback((next: DurationPreset): void => {
    setPreset(next);
    if (next !== 'custom') setCustomDate(undefined);
  }, []);

  const handleConfirm = useCallback((): void => {
    if (!copy.needsDuration) {
      onConfirm();
      return;
    }
    const iso = resolveDurationIso(preset, customDate);
    onConfirm(action === 'mute' ? { mutedUntil: iso } : { excludedUntil: iso });
  }, [copy.needsDuration, preset, customDate, action, onConfirm]);

  const confirmDisabled =
    isPending ||
    (copy.needsDuration && preset === 'custom' && customDate === undefined);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle
            className={cn(
              copy.destructive && 'flex items-center gap-2 text-destructive'
            )}
          >
            {copy.destructive && (
              <AlertTriangle className="size-5" aria-hidden="true" />
            )}
            {copy.title}
          </DialogTitle>
          {copy.subtitle && (
            <DialogDescription>{copy.subtitle}</DialogDescription>
          )}
        </DialogHeader>

        <div className="flex flex-col gap-3 py-1 text-sm">
          {copy.body && <p className="text-muted-foreground">{copy.body}</p>}

          {copy.needsDuration && (
            <div className="flex flex-col gap-2">
              <span className="font-medium">
                {action === 'mute' ? 'Mute until…' : 'Exclude until…'}
              </span>
              <div
                className="flex flex-wrap gap-2"
                role="radiogroup"
                aria-label={action === 'mute' ? 'Mute until' : 'Exclude until'}
              >
                {DURATION_PRESETS.map(p => (
                  <Button
                    key={p.key}
                    type="button"
                    size="sm"
                    variant="outline"
                    // Single-select duration → expose radio semantics so AT users
                    // can tell which preset is active (the visual fill alone isn't
                    // announced).
                    role="radio"
                    aria-checked={preset === p.key}
                    // Selected pill is solid neutral-black (#1f1f1f) per mockup,
                    // not the default (primary) variant. Inverts in dark mode so
                    // it stays legible on the dark modal.
                    className={cn(
                      // Use the app's standard outline/ghost hover affordance
                      // (`bg-accent`, theme-aware in dark) for responsiveness, but
                      // keep text neutral instead of the loud sky-blue
                      // `accent-foreground` the variant would apply.
                      'hover:bg-accent hover:text-foreground',
                      preset === p.key &&
                        'border-[#1f1f1f] bg-[#1f1f1f] text-white hover:bg-[#1f1f1f]/90 hover:text-white dark:border-white dark:bg-white dark:text-[#1f1f1f] dark:hover:bg-white/90 dark:hover:text-[#1f1f1f]'
                    )}
                    onClick={() => selectPreset(p.key)}
                  >
                    {p.label}
                  </Button>
                ))}
              </div>
              {preset === 'custom' && (
                <div className="flex flex-col gap-2">
                  <span className="text-muted-foreground">
                    {action === 'mute'
                      ? 'Pick the day this alert should be muted through. The mute expires at end-of-day UTC.'
                      : 'Pick the day this alert should be excluded through. The exclusion expires at end-of-day UTC.'}
                  </span>
                  {/* Day-button ghost hover turns text sky-blue
                      (--accent-foreground). Keep the app's standard `bg-accent`
                      hover affordance (theme-aware in dark) but neutral text;
                      the selected day keeps its black (primary) fill. Scoped to
                      this calendar — not the shared component. */}
                  <div className="flex justify-center [&_button:not([data-selected-single=true]):hover]:bg-accent [&_button:not([data-selected-single=true]):hover]:text-foreground [&_button[data-selected-single=true]:hover]:bg-primary [&_button[data-selected-single=true]:hover]:text-primary-foreground">
                    <Calendar
                      mode="single"
                      selected={customDate}
                      onSelect={setCustomDate}
                      disabled={{ before: minDate }}
                      autoFocus
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Both count lines are muted/grey per mockup. */}
          <p className="text-muted-foreground">{copy.countLine}</p>
          {copy.alreadyLine && (
            <p className="text-muted-foreground">{copy.alreadyLine}</p>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant={copy.destructive ? 'destructive' : 'default'}
            onClick={handleConfirm}
            disabled={confirmDisabled}
          >
            {isPending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              copy.destructive && <ShieldOff className="size-4" />
            )}
            {copy.confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

BulkActionConfirmDialog.displayName = 'BulkActionConfirmDialog';
